import { useMemo, useState, useEffect } from "react";
import {
  buildAllItems,
  MASTERY_PRESETS,
  EXERCISE_SKILL_NODES,
  MVP_NODES,
  getEligibleNewNodes,
  unlockedNodes,
  visibleNodes,
  visibleItems,
  resolveActivePlan,
  resolveGenerator,
  effectivePlanTemplate,
  STANDARD_TEMPLATE,
  type CurriculumNode,
  type CardStore,
  type MasteryThresholds,
  type MasteryPreset,
  type ReviewSummary,
  type Item,
  type PlanTemplate,
  type SavedPlan,
  type PlanGenerator,
  type PlanDependencies,
} from "@music-app/learning-system";
import {
  useCardsStore,
  usePrefsStore,
  useSavedPlansStore,
  loadAllReviews,
  loadNodeExposures,
  type ReviewRecord,
} from "@music-app/persistence";
import { filterByPrefs } from "../routes/session-prefs.js";

const TRACK_LABELS: Record<CurriculumNode["track"], string> = {
  pitch: "Pitch",
  modes: "Modes",
  intervals: "Intervals",
  harmony: "Harmony",
  dictation: "Dictation",
  counterpoint: "Counterpoint",
  guitar: "Guitar",
};

export interface PracticeData {
  cards: CardStore;
  cardsLoaded: boolean;
  items: Item[];
  thresholds: MasteryThresholds;
  reviews: ReviewSummary[];
  prefsLoaded: boolean;
  sessionTargetMinutes: number;
  setSessionTargetMinutes: (v: number) => Promise<void>;
  sessionMix: "review-only" | "balanced" | "new-focus";
  setSessionMix: (v: "review-only" | "balanced" | "new-focus") => Promise<void>;
  newSkillNodeId: string | null;
  setNewSkillNodeId: (v: string | null) => Promise<void>;
  includeLeadingTone: boolean;
  onboardingComplete: boolean;
  placementCompleted: boolean;
  masteryPreset: MasteryPreset;
  unlockEverything: boolean;
  eligibleNewNodes: CurriculumNode[];
  focusTracks: { track: CurriculumNode["track"]; label: string; nodes: CurriculumNode[] }[];
  nodeExposures: Map<string, number>;
  /** @deprecated Use activePlan + generator instead. Kept for PracticeCustomize / Explain / Why. */
  activePlanTemplate: PlanTemplate;
  activePlan: SavedPlan;
  generator: PlanGenerator;
  deps: PlanDependencies;
}

export function usePracticeData(): PracticeData {
  const cards = useCardsStore((s) => s.cards);
  const cardsLoaded = useCardsStore((s) => s.loaded);

  const includeLeadingTone = usePrefsStore((s) => s.includeLeadingTone);
  const onboardingComplete = usePrefsStore((s) => s.onboardingComplete);
  const placementCompleted = usePrefsStore((s) => s.placementCompleted);
  const sessionTargetMinutes = usePrefsStore((s) => s.sessionTargetMinutes);
  const setSessionTargetMinutes = usePrefsStore((s) => s.setSessionTargetMinutes);
  const sessionMix = usePrefsStore((s) => s.sessionMix);
  const setSessionMix = usePrefsStore((s) => s.setSessionMix);
  const newSkillNodeId = usePrefsStore((s) => s.newSkillNodeId);
  const setNewSkillNodeId = usePrefsStore((s) => s.setNewSkillNodeId);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const activeSavedPlanId = usePrefsStore((s) => s.activeSavedPlanId);
  const prefsLoaded = usePrefsStore((s) => s.loaded);
  const userPlans = useSavedPlansStore((s) => s.plans);
  const unlockEverything = usePrefsStore((s) => s.unlockEverything);
  const enabledInstrumentTracks = usePrefsStore((s) => s.enabledInstrumentTracks);

  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  useEffect(() => {
    loadAllReviews()
      .then((recs: ReviewRecord[]) =>
        setReviews(
          recs.map((r) => ({
            itemId: r.itemId,
            correct: r.correct,
            timestamp: r.timestamp,
            source: r.source,
          })),
        ),
      )
      .catch(() => { /* non-fatal */ });
  }, []);

  const [nodeExposures, setNodeExposures] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    loadNodeExposures(EXERCISE_SKILL_NODES).then(setNodeExposures).catch(() => { /* non-fatal */ });
  }, []);

  // Gate seam: drop dormant instrument-track items/nodes so opt-in content
  // (e.g. guitar) never enters the daily plan or node lists until enabled.
  const nodes = useMemo(
    () => visibleNodes(MVP_NODES, enabledInstrumentTracks),
    [enabledInstrumentTracks],
  );

  const items = useMemo(
    () => visibleItems(filterByPrefs(buildAllItems(), { includeLeadingTone }), enabledInstrumentTracks),
    [includeLeadingTone, enabledInstrumentTracks],
  );

  const thresholds = MASTERY_PRESETS[masteryPreset];

  const activePlan = useMemo(
    () => resolveActivePlan(activeSavedPlanId, userPlans),
    [activeSavedPlanId, userPlans],
  );

  const generator = useMemo(
    () => resolveGenerator(activePlan.generatorId),
    [activePlan.generatorId],
  );

  // Backward-compat for PracticeWhy / PracticeExplain / PracticeCustomize
  // which still call buildCoachPlanWithTrace(activePlanTemplate, ...).
  const activePlanTemplate = useMemo(
    () => effectivePlanTemplate(generator, activePlan) ?? STANDARD_TEMPLATE,
    [generator, activePlan],
  );

  const eligibleNewNodes = useMemo(
    () => getEligibleNewNodes(nodes, items, cards),
    [nodes, items, cards],
  );

  const focusTracks = useMemo(() => {
    const unlocked = unlockedNodes(nodes, items, cards, {
      forceAll: unlockEverything,
      thresholds,
    });
    const seen = new Set<CurriculumNode["track"]>();
    const order: CurriculumNode["track"][] = [];
    for (const node of nodes) {
      if (!seen.has(node.track)) {
        seen.add(node.track);
        order.push(node.track);
      }
    }
    return order
      .map((track) => ({
        track,
        label: TRACK_LABELS[track],
        nodes: unlocked.filter((n) => n.track === track),
      }))
      .filter((t) => t.nodes.length > 0);
  }, [nodes, items, cards, unlockEverything, thresholds]);

  const deps: PlanDependencies = useMemo(
    () => ({
      items,
      allNodes: nodes,
      cards,
      thresholds,
      reviews,
      nodeExposures,
      forceAcquired: unlockEverything,
      now: new Date(),
    }),
    [items, nodes, cards, thresholds, reviews, nodeExposures, unlockEverything],
  );

  return {
    cards,
    cardsLoaded,
    items,
    thresholds,
    reviews,
    prefsLoaded,
    sessionTargetMinutes,
    setSessionTargetMinutes,
    sessionMix,
    setSessionMix,
    newSkillNodeId,
    setNewSkillNodeId,
    includeLeadingTone,
    onboardingComplete,
    placementCompleted,
    masteryPreset,
    unlockEverything,
    eligibleNewNodes,
    focusTracks,
    nodeExposures,
    activePlanTemplate,
    activePlan,
    generator,
    deps,
  };
}
